import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "@/components/common/Spinner";
import { useLocation, useNavigate, useParams } from "react-router";
import { Icon } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { ContextMenu } from "@/components/common/ContextMenu";
import { FilterButton } from "@/components/common/FilterButton";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { IncludeArchivedCheckbox } from "@/components/common/IncludeArchivedCheckbox";
import { useAgentSessionContext } from "@/components/chat/AgentSessionContext";
import {
	ENVIRONMENT_CARD_GRID_COLUMNS,
	EnvironmentCard,
} from "@/components/environments/EnvironmentCard";
import { type EnvironmentStatusLoadState } from "@/components/environments/EnvironmentStatusBadge";
import {
	deleteProject,
	isRunningStatus,
	listEnvironments,
	listEnvironmentStatuses,
	listProjects,
	MAX_RUNNING_ENVIRONMENTS_PER_ORG,
	unarchiveProject,
	type Environment,
	type EnvironmentStatusEntry,
	type Project,
} from "@/lib/api";
import { formatRelativeDateTime } from "@/lib/format-datetime";
import { writeStoredNewChatEnvironmentSelection } from "@/lib/new-chat-environment-selection";
import { useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useHasPermission } from "@/lib/permissions";
import { useProjectFilterStore } from "@/stores/useProjectFilterStore";
import { useToastStore } from "@/stores/useToastStore";
import { confirmLeaveWithUnsavedFiles } from "@/stores/useUnsavedChangesGuardStore";
import { ProjectDialog } from "./ProjectDialog";
import { LaunchEnvironmentDialog } from "./LaunchEnvironmentDialog";
import { useRunnerOnlinePolling } from "./useRunnerOnlinePolling";

/**
 * プロジェクトのメインビュー。上部のプロジェクトセレクタで選んだプロジェクトについて、
 * プロジェクト情報 (編集・アーカイブ) と所属する環境を表示する。
 * 選択中のプロジェクトは URL (/projects/:projectId) と同期する。
 */
export function ProjectsPage() {
	const navigate = useNavigate();
	const canManageProjects = useHasPermission("manageProjects");
	const { projectId: urlProjectId } = useParams<{ projectId: string }>();

	const includeArchived = useProjectFilterStore((s) => s.includeArchived);
	const setIncludeArchived = useProjectFilterStore((s) => s.setIncludeArchived);
	const lastProjectId = useProjectFilterStore((s) => s.lastProjectId);
	const setLastProjectId = useProjectFilterStore((s) => s.setLastProjectId);

	const [projects, setProjects] = useState<Project[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<Project | null>(null);
	const [pendingDelete, setPendingDelete] = useState<Project | null>(null);

	useEffect(() => {
		let cancelled = false;
		setError(null);
		listProjects({ includeArchived })
			.then((data) => {
				if (!cancelled) setProjects(data);
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [refreshKey, includeArchived]);

	// URL の ID が最優先。ID なし (サイドバーからの遷移) や一覧にない ID なら、直前に表示していた
	// プロジェクトへ戻し、それもなければ先頭にする。
	const selectedProject =
		projects.find((p) => p.id === urlProjectId) ??
		projects.find((p) => p.id === lastProjectId) ??
		projects[0] ??
		null;

	// URL とセレクタの選択を揃える。未指定・存在しない ID なら選択中プロジェクトの URL へ置き換える。
	// チャット等の別ルートへ遷移した直後もアンマウントまでの間このエフェクトが走るため、
	// プロジェクトルート表示中のみ同期する (でないと遷移先の URL をここで上書きしてしまう)。
	const { pathname } = useLocation();
	useEffect(() => {
		if (!pathname.startsWith("/projects")) return;
		if (loading || selectedProject === null) return;
		setLastProjectId(selectedProject.id);
		if (urlProjectId !== selectedProject.id) {
			navigate(`/projects/${selectedProject.id}`, { replace: true });
		}
	}, [pathname, loading, selectedProject, urlProjectId, navigate, setLastProjectId]);

	const handleDeleteConfirm = async () => {
		if (!pendingDelete) return;
		// 稼働中の環境インスタンスがあるとサーバーが拒否する。失敗はそのまま伝播させ、
		// ConfirmDialog がその理由をダイアログ内に表示する (選択は保たれる)。
		await deleteProject(pendingDelete.id);
		useToastStore.getState().show({
			type: "success",
			message: `プロジェクト「${pendingDelete.name}」をアーカイブしました。`,
		});
		// 再取得を待つ間にアーカイブ済みプロジェクトへリダイレクトしないよう、先にローカル状態から除く。
		setProjects((prev) => prev.filter((p) => p.id !== pendingDelete.id));
		setPendingDelete(null);
		setRefreshKey((k) => k + 1);
		navigate("/projects", { replace: true });
	};

	const handleUnarchive = async (project: Project) => {
		let restored: Project;
		try {
			restored = await unarchiveProject(project.id);
		} catch (err) {
			useToastStore.getState().show({
				type: "error",
				message: err instanceof Error ? err.message : "プロジェクトの復元に失敗しました。",
			});
			throw err;
		}
		useToastStore.getState().show({
			type: "success",
			message: `プロジェクト「${restored.name}」のアーカイブを解除しました。`,
		});
		// 再取得を待たずに復元結果を反映する (アーカイブ済みを表示していない場合も選択を保つ)。
		setProjects((prev) => prev.map((p) => (p.id === restored.id ? restored : p)));
		setRefreshKey((k) => k + 1);
	};

	return (
		<div
			data-testid="projects-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 16,
					height: 40,
					flexShrink: 0,
					marginBottom: 24,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
					<ProjectSelector
						projects={projects}
						selected={selectedProject}
						onSelect={(project) => navigate(`/projects/${project.id}`)}
					/>
					<IncludeArchivedCheckbox
						checked={includeArchived}
						testId="project-include-archived"
						onChange={setIncludeArchived}
					/>
				</div>
				{canManageProjects && (
					<span style={{ display: "flex", minWidth: 0 }}>
						<ConfirmButton
							label="新規プロジェクト"
							icon="plus"
							loading={false}
							variant="primary"
							size="sm"
							onClick={() => {
								setEditing(null);
								setDialogOpen(true);
							}}
							data-testid="project-create-button"
						/>
					</span>
				)}
			</div>

			<ProjectDialog
				open={dialogOpen}
				project={editing}
				onClose={() => setDialogOpen(false)}
				onSaved={(saved) => {
					// 再取得を待たずに保存結果を選択できるよう、先にローカル状態へ反映する。
					setProjects((prev) =>
						prev.some((p) => p.id === saved.id)
							? prev.map((p) => (p.id === saved.id ? saved : p))
							: [saved, ...prev],
					);
					setRefreshKey((k) => k + 1);
					navigate(`/projects/${saved.id}`, { replace: true });
				}}
			/>
			<ConfirmDialog
				open={pendingDelete !== null}
				title="プロジェクトをアーカイブ"
				message={`「${pendingDelete?.name}」をアーカイブしますか？`}
				confirmLabel="アーカイブ"
				cancelLabel="キャンセル"
				onConfirm={handleDeleteConfirm}
				onCancel={() => setPendingDelete(null)}
			/>

			{error && <p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{error}</p>}

			{!error && loading && <CenteredSpinner />}

			{!error && !loading && projects.length === 0 && (
				<p
					style={{
						color: "var(--fg-3)",
						fontSize: "var(--text-ui)",
						textAlign: "center",
						padding: "48px 0",
					}}
				>
					プロジェクトがありません。「新規プロジェクト」から作成できます。
				</p>
			)}

			{!error && !loading && selectedProject && (
				<ProjectDetail
					key={selectedProject.id}
					project={selectedProject}
					// アーカイブ済みのプロジェクトはサーバー側で変更できないため操作を伏せる。
					readOnlyReason={
						selectedProject.archivedAt === null
							? null
							: "アーカイブ済みのプロジェクトは変更できません。"
					}
					onEdit={() => {
						setEditing(selectedProject);
						setDialogOpen(true);
					}}
					onDelete={() => setPendingDelete(selectedProject)}
					onUnarchive={() => handleUnarchive(selectedProject)}
				/>
			)}
		</div>
	);
}

/** プロジェクトサマリー左端のセレクタ。クリックでプロジェクト一覧のメニューを開く。 */
function ProjectSelector({
	projects,
	selected,
	onSelect,
}: {
	projects: Project[];
	selected: Project | null;
	onSelect: (project: Project) => void;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);

	return (
		<>
			<FilterButton
				open={menuOpen}
				disabled={projects.length === 0}
				ref={anchorRef}
				icon="rocket"
				label={selected?.name ?? "プロジェクトがありません"}
				testId="project-selector"
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && (
				<ContextMenu
					position={{ anchor: anchorRef.current, align: "left" }}
					onClose={() => setMenuOpen(false)}
					testId="project-selector-menu"
					minWidth={220}
					items={projects.map((project) => ({
						key: project.id,
						label: project.archivedAt === null ? project.name : `${project.name} (アーカイブ済み)`,
						selectable: true,
						selected: project.id === selected?.id,
						testId: `project-selector-item-${project.id}`,
						onSelect: () => onSelect(project),
					}))}
				/>
			)}
		</>
	);
}

const PROJECT_ACTION_SIZE = 28;

/**
 * 選択中プロジェクトの詳細 (プロジェクト情報・環境・チャット)。
 * <code>readOnlyReason</code> が非 null なら変更操作を無効にし、その理由をツールチップに出す。
 * アーカイブ済みの場合は編集・アーカイブの代わりにアーカイブ解除を出す。
 */
function ProjectDetail({
	project,
	readOnlyReason,
	onEdit,
	onDelete,
	onUnarchive,
}: {
	project: Project;
	readOnlyReason: string | null;
	onEdit: () => void;
	onDelete: () => void;
	onUnarchive: () => void;
}) {
	const archivedAt = project.archivedAt;
	const canManageProjects = useHasPermission("manageProjects");
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
			<section
				data-testid="project-detail-info"
				style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
					<h2
						style={{
							fontSize: "var(--text-display)",
							fontWeight: 600,
							letterSpacing: "var(--tracking-snug)",
							color: "var(--fg-1)",
							margin: 0,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
						title={project.name}
						data-tooltip-if-truncated
					>
						{project.name}
					</h2>
					{archivedAt !== null && (
						<span
							data-testid="project-archived-badge"
							style={{
								flexShrink: 0,
								padding: "2px 8px",
								borderRadius: "var(--radius-full)",
								background: "var(--surface-2)",
								color: "var(--fg-2)",
								fontSize: "var(--text-caption)",
								fontWeight: 500,
							}}
							title={`${formatRelativeDateTime(archivedAt)}にアーカイブ`}
						>
							アーカイブ済み
						</span>
					)}
					{/* 名前と操作の間はアイコン 1 つ分あけ、名前の一部に見えないようにする。 */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							flexShrink: 0,
							marginLeft: PROJECT_ACTION_SIZE,
						}}
					>
						{!canManageProjects ? null : archivedAt !== null ? (
							<IconButton
								icon="undo"
								title="アーカイブを解除"
								data-testid="project-unarchive-button"
								size={PROJECT_ACTION_SIZE}
								onClick={onUnarchive}
							/>
						) : (
							<>
								<IconButton
									icon="pencil"
									title={readOnlyReason ?? "プロジェクトを編集"}
									data-testid="project-edit-button"
									size={PROJECT_ACTION_SIZE}
									disabled={readOnlyReason !== null}
									onClick={onEdit}
								/>
								<IconButton
									icon="trash"
									title={readOnlyReason ?? "プロジェクトをアーカイブ"}
									data-testid="project-delete-button"
									size={PROJECT_ACTION_SIZE}
									hoverColor="var(--danger)"
									disabled={readOnlyReason !== null}
									onClick={onDelete}
								/>
							</>
						)}
					</div>
				</div>
				<span
					style={{
						fontSize: "var(--text-caption)",
						fontVariantNumeric: "tabular-nums",
						color: "var(--fg-3)",
					}}
				>
					作成 {formatRelativeDateTime(project.createdAt)} ・ 更新{" "}
					{formatRelativeDateTime(project.updatedAt)}
				</span>
			</section>

			<ProjectEnvironmentsSection projectId={project.id} readOnlyReason={readOnlyReason} />
		</div>
	);
}

/**
 * 選択中プロジェクトの環境一覧。新規インスタンスの作成と、状態バッジの再取得ができる。
 * <code>readOnlyReason</code> が非 null なら起動を無効にし、その理由を表示する。
 */
function ProjectEnvironmentsSection({
	projectId,
	readOnlyReason,
}: {
	projectId: string;
	readOnlyReason: string | null;
}) {
	const navigate = useNavigate();
	const { startNewChatDraft } = useAgentSessionContext();
	const [environments, setEnvironments] = useState<Environment[]>([]);

	const handleNewChat = useCallback(
		async (environment: Environment) => {
			if (!(await confirmLeaveWithUnsavedFiles())) return;
			// バックエンドにチャットを作らず、このエージェントを選んだ作成ビューへ移動するだけにする。
			// 実際のチャット作成は最初のメッセージ送信時に行う (CenterPane.handleSend)。
			// 送信先は NewChatEnvironmentStatus が稼働中と確かめてから決める。
			startNewChatDraft();
			useChatStore.getState().setNewChatAgent(environment);
			writeStoredNewChatEnvironmentSelection({ projectId, environmentId: environment.id });
			useLayoutStore.getState().requestComposerFocus();
			navigate("/chats/new");
		},
		[navigate, projectId, startNewChatDraft],
	);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [statuses, setStatuses] = useState<Map<string, EnvironmentStatusEntry>>(new Map());
	const [statusState, setStatusState] = useState<EnvironmentStatusLoadState>("loading");
	const [refreshKey, setRefreshKey] = useState(0);
	const [launchOpen, setLaunchOpen] = useState(false);

	useEffect(() => {
		let cancelled = false;
		// 初回のみセクション全体のスピナーを出す。更新ボタンでは一覧を出したまま静かに差し替える。
		if (refreshKey === 0) setLoading(true);
		setError(null);
		listEnvironments()
			.then((data) => {
				if (!cancelled) setEnvironments(data);
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [refreshKey]);

	// 状態は一覧とは独立に取得し、各カードのバッジへ後追いで反映する (一覧描画をブロックしない)。
	// コンテナ状態の遷移 (起動準備中 → 稼働中など) は更新ボタンで再取得する。取得失敗時はバッジが「不明」になる。
	useEffect(() => {
		let cancelled = false;
		setStatusState("loading");
		listEnvironmentStatuses()
			.then((entries) => {
				if (cancelled) return;
				setStatuses(new Map(entries.map((e) => [e.environmentId, e])));
				setStatusState("loaded");
			})
			.catch(() => {
				if (!cancelled) setStatusState("error");
			});
		return () => {
			cancelled = true;
		};
	}, [refreshKey]);

	const projectEnvironments = useMemo(
		() => environments.filter((env) => env.projectId === projectId),
		[environments, projectId],
	);

	const hasRunnerEnvironment = projectEnvironments.some((env) => env.runner != null);
	const runnerOnlineUpdateFailed = useRunnerOnlinePolling(hasRunnerEnvironment, setStatuses);

	// 上限は組織単位・稼働中のクラウドの環境だけが対象 (デバイスの環境は数えない) のため、
	// プロジェクトで絞らない組織全体の状態で判定する。
	// 状態の取得に失敗している間 (statuses が空) は上限に達していない扱いにし、起動はサーバーが弾く。
	const runningCount = useMemo(
		() =>
			environments.filter(
				(env) => env.runner === null && isRunningStatus(statuses.get(env.id)?.status),
			).length,
		[environments, statuses],
	);
	const atRunningLimit = runningCount >= MAX_RUNNING_ENVIRONMENTS_PER_ORG;

	// クレジット枯渇中はサーバーの起動 gate がクラウドの環境を拒否するため、UI 側でも起動系の操作を無効にする。
	const creditExhausted = useCurrentUserStore((s) => s.creditExhausted);
	const canManageAgents = useHasPermission("manageAgents");
	const creditExhaustedReason = creditExhausted
		? "組織のクレジット残高が不足しているため、エージェントを起動できません。クレジットが付与されると再度起動できます。"
		: null;

	return (
		<section data-testid="project-environments">
			<SectionHeading title="エージェント">
				<div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
					<IconButton
						icon="rotate"
						title="エージェントの状態を更新"
						data-testid="project-environments-refresh"
						size={28}
						disabled={statusState === "loading"}
						onClick={() => setRefreshKey((k) => k + 1)}
					/>
					{/* ConfirmButton は title を持たないため、無効の理由は外側の要素に付ける。 */}
					{canManageAgents && (
						<span title={readOnlyReason ?? undefined} style={{ display: "flex", minWidth: 0 }}>
							<ConfirmButton
								label="新規エージェント"
								icon="plus"
								loading={false}
								variant="primary"
								size="sm"
								disabled={readOnlyReason !== null}
								onClick={() => setLaunchOpen(true)}
								data-testid="project-launch-environment-button"
							/>
						</span>
					)}
				</div>
			</SectionHeading>
			{runnerOnlineUpdateFailed && (
				<p
					data-testid="runner-online-update-error"
					style={{ color: "var(--warning)", fontSize: "var(--text-ui)", padding: "4px 0 8px" }}
				>
					デバイスの接続状態を更新できません。しばらくすると自動で再試行します。
				</p>
			)}
			<LaunchEnvironmentDialog
				open={launchOpen}
				projectId={projectId}
				onClose={() => setLaunchOpen(false)}
				onCreated={(created) => {
					useToastStore.getState().show({
						type: "success",
						message: `エージェント「${created.name}」を作成しました。`,
					});
					setRefreshKey((k) => k + 1);
				}}
			/>
			{atRunningLimit && (
				<p
					data-testid="environment-running-limit-note"
					style={{ color: "var(--fg-3)", fontSize: "var(--text-ui)", padding: "4px 0 8px" }}
				>
					{`同時に稼働できるエージェントは1組織につき最大${MAX_RUNNING_ENVIRONMENTS_PER_ORG}個までです。上限に達しているため、他のエージェントを停止するまで新しく起動できません。`}
				</p>
			)}
			{error && <p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{error}</p>}
			{!error && loading && <CenteredSpinner />}
			{!error && !loading && projectEnvironments.length === 0 && (
				<div
					data-testid="project-environments-empty"
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 8,
						padding: "48px 16px",
						textAlign: "center",
						color: "var(--fg-3)",
						fontSize: "var(--text-ui)",
					}}
				>
					<Icon name="agent" size={56} />
					<p style={{ margin: 0 }}>
						このプロジェクトのエージェントはありません。「新規エージェント」から作成できます。
					</p>
				</div>
			)}
			{!error && !loading && projectEnvironments.length > 0 && (
				<div
					style={{
						display: "grid",
						gridTemplateColumns: ENVIRONMENT_CARD_GRID_COLUMNS,
						gap: 16,
					}}
				>
					{projectEnvironments.map((env) => (
						<EnvironmentCard
							key={env.id}
							environment={env}
							status={statuses.get(env.id)?.status}
							statusState={statusState}
							// 稼働中の自分自身は上限に数えない (バックエンドの HasFreeSlotAsync と揃える)。
							// これにより、上限ちょうどで稼働中の環境自身の再起動が誤って無効化されない。
							startDisabledReason={
								readOnlyReason ??
								(env.runner == null ? creditExhaustedReason : null) ??
								(env.runner === null &&
								runningCount - (isRunningStatus(statuses.get(env.id)?.status) ? 1 : 0) >=
									MAX_RUNNING_ENVIRONMENTS_PER_ORG
									? `同時に稼働できるエージェントは1組織につき最大${MAX_RUNNING_ENVIRONMENTS_PER_ORG}個までです。`
									: null)
							}
							errorReason={statuses.get(env.id)?.errorReason}
							failureDetail={statuses.get(env.id)?.failureDetail}
							runnerOnline={statuses.get(env.id)?.runnerOnline}
							scheduledRestartAt={statuses.get(env.id)?.scheduledRestartAt}
							onUpdated={(renamed) =>
								setEnvironments((prev) => prev.map((e) => (e.id === renamed.id ? renamed : e)))
							}
							onStatusChanged={(entry) => {
								setStatuses((prev) =>
									new Map(prev).set(entry.environmentId, {
										...entry,
										runnerOnline: entry.runnerOnline ?? prev.get(entry.environmentId)?.runnerOnline,
									}),
								);
								// 開いているチャットの環境なら、チャットへ戻ったときに再取得を待たず反映する。
								useChatStore
									.getState()
									.setEnvironmentStatus(
										entry.environmentId,
										entry.status,
										entry.scheduledRestartAt,
									);
							}}
							onDeleted={(environmentId) =>
								setEnvironments((prev) => prev.filter((e) => e.id !== environmentId))
							}
							onNewChat={handleNewChat}
						/>
					))}
				</div>
			)}
		</section>
	);
}

/** セクション見出し。右側に操作ボタンなどを置ける。 */
function SectionHeading({ title, children }: { title: string; children?: React.ReactNode }) {
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				gap: 12,
				marginBottom: 12,
			}}
		>
			<h3
				style={{
					flexShrink: 0,
					whiteSpace: "nowrap",
					fontSize: "var(--text-ui)",
					fontWeight: 600,
					color: "var(--fg-2)",
					letterSpacing: "var(--tracking-heading)",
					margin: 0,
				}}
			>
				{title}
			</h3>
			{children}
		</div>
	);
}

function CenteredSpinner() {
	return (
		<div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
			<Spinner size={16} />
		</div>
	);
}
